/**
 * @lume/shared — Visibilidade por papel×contraparte (Motor Neutro B4).
 *
 * Camada PURA de COMPARTIMENTAÇÃO DE AUDIÊNCIA dentro de um vínculo: o que cada papel
 * VÊ do outro (caucus de mediação, sigilo financiador≠beneficiário, compartimentação).
 * Neutraliza o *commercial-shape* que o motor tem hoje: `BubbleVisibilityService.sideToFaction`
 * crava `seller→'faction-a'` / `buyer→'faction-b'` — só **2 facções**, derivadas de um enum
 * de transação comercial. Mediação de 3+ partes, financiamento (financiador/beneficiário/
 * prestador) e condomínio N-unidades não são representáveis (spec §0 PV1).
 *
 * Aqui a audiência é um **compartimento** — código OPACO de string — resolvido a partir do
 * **papel por-instância** (`rawRole`, a neutralização que a B1 fez do achatamento telecom),
 * com N compartimentos por vínculo.
 *
 * ★ VIVO desde a fatia B4b (era DORMENTE na B4). `BubbleVisibilityService.canAccessContext`
 * consome `resolveVisibilityDecision` como fonte-de-verdade da decisão, e `prepareOutbound`
 * compõe as chaves por `composeCompartmentKeys`. A implementação anterior do serviço é
 * PRESERVADA como `canAccessLegacyBaseline` (`@internal`, cache-free) — o baseline
 * falsificável dos dois differentials; sem ela a comparação viraria tautológica (o furo que a
 * revisão adversarial pegou na fatia A4/I1). A limpeza do legado é fatia posterior, após a
 * validação viva.
 *
 * ★ A B4b NÃO é byte-idêntica — e é o único ponto do programa onde isso vale, porque a
 * mudança **só ESTREITA audiência sobre todo dado alcançável**. A claim é ESCOPADA de
 * propósito (a revisão adversarial confirmou por execução que ela não é incondicional): os
 * DOIS flips `deny→allow` existentes vivem em chaves que **nenhum writer produz** —
 * `bond-role:<papel>` (vacância garantida mecanicamente pelo teste de ALFABETO em
 * `compartment-writer.spec.ts`; divergência (c) medida no differential) e
 * `faction:<código cunhado por policy>` (só em mensagem escrita PÓS-B4b sob policy, cuja
 * audiência é subconjunto ESTRITO da que o writer antigo daria). No dado existente:
 *   (a) o piso R6 (`bond-anchors` subordinada ao compartimento) transforma `allow` em `deny`,
 *       nunca o inverso — e cobre também as mensagens compartimentadas JÁ existentes, sem
 *       reescrever dado;
 *   (b) o writer passa a emitir MENOS chaves;
 *   (c) o efeito é inerte sobre o dado atual, verificado por query e não presumido (spec §0
 *       PW6: 68.689 mensagens no dev, ZERO com chave `faction:`; 0 participantes
 *       `seller`/`buyer`; todas as conversas com `sessionContext = NULL`).
 *
 * ★ ESPELHO FIEL, NÃO "LIMPO" — a assimetria PV8 é DELIBERADAMENTE reproduzida:
 *   `side === null` (compartimento NÃO declarado) **PASSA** na Camada 4 (caucus —
 *   `bubble-visibility.service.ts:268`) e **NEGA** na chave `faction:` (`:213-215`).
 *   Não é bug desta camada; é o comportamento vivo. Corrigi-lo mudaria conduta ⇒ residual
 *   declarado (spec §5 R1), congelado por teste aqui e no differential.
 *
 * ★ ORTOGONAL AO PORTEIRO (spec §0 PV5): visibilidade NÃO entra na decisão "qual assurance
 * é exigida". Nada aqui referencia `evaluateIdentityGate`/`AccessDecision`/`risk-category`,
 * e a Frente A não referencia isto. A Fase 2 (estagio4, capacidade neutra nº 6) é explícita:
 * «visibilidade/compartimentação por papel×contraparte, ORTOGONAL ao assurance do sujeito».
 *
 * ★ JAMAIS sobrecarregar `adversarialMode` (P9 — 4 valores, 12+ consumidores, bloqueia H_c via
 * AutonomyGateService/ADR-040). Ele entra aqui SÓ como o liga/desliga da camada, exatamente
 * como o serviço faz hoje.
 *
 * ★ PRECONDIÇÕES DA PROMOÇÃO — estado após a B4b: **FECHADAS (1) (2) (4) (5) (7)** + o
 * CONTRATO DO SNAPSHOT; **ABERTAS (3)** `participant:<personId>` morto e **(6)** caucus ↔
 * compartimento novo (= fatia B4c). O texto original está preservado, com o veredito por item.
 *
 *   ✅ FECHADA (B4b) — (1) **Sombreamento de namespace:** ao tornar `bond-role:` NATIVA, uma `ConfidentialityBubble`
 *       cujo `visibilityKey` literal comece com `bond-role:` deixa de ser consultada (a nativa
 *       decide antes). **O MESMO vale para `faction:<código que a config possa cunhar>`:**
 *       verificado por execução — sem policy a chave `faction:compartment-a` é não-nativa e uma
 *       bubble homônima que NEGA decide (deny); com a policy que cunha `compartment-a` a chave
 *       vira nativa e CONCEDE, sombreando a bubble. Rejeitar prefixos reservados no CRUD de
 *       bubble ANTES de promover.
 *   ✅ FECHADA (B4b — 400 mecânico no `PUT .../visibility-policy`; audit `high` nas DUAS formas
 *       de reagrupamento: papel já-mapeado que muda de código **e** papel NOVO que herda um
 *       código existente [achado I3 da revisão — a 1ª versão só pegava a primeira, e o
 *       mitigador falhava na classe que ele nomeia]. A APROVAÇÃO formal molde
 *       `classifyMappingRisk` segue na Frente D) — (2) **Autoria com portão:** quando existir superfície de autoria da `visibilityPolicy`, ela
 *       precisa do portão molde `classifyMappingRisk` (A3). O piso abaixo já barra AMPLIAÇÃO;
 *       *remapear* compartimentos entre papéis ainda merece aprovação/auditoria.
 *   ⛔ ABERTA — (3) **R3 `participant:<personId>` está MORTO no serviço** (`checkBubbleAccess` passa `null`
 *       com o comentário "User model has no personId field"). Este resolver ACEITA `personId`,
 *       mas o differential o alimenta com `null` para ser fiel ⇒ o ramo só acorda na promoção.
 *   ✅ FECHADA (B4b — `invalidateAllMessages`, chamado no write da policy) — (4) **Cache:** o TTL de 30s do serviço é por `(tenant, message, user)` e não conhece
 *       invalidação por mudança de `Bond.metadata` ⇒ a promoção deve invalidar no write da policy.
 *   ✅ FECHADA (B4b — É A FATIA) — (5) **WRITER compartment-aware — a precondição que DESTRAVA o valor da frente.**
 *       `prepareOutbound` co-emite `bond-anchors` (e `bond-admins`) em TODA mensagem
 *       compartimentada (`:477`/`:485`/`:503`); `bond-anchors` libera QUALQUER participante
 *       ativo no OR-mode da Camada 5, e `bond-admins` faz o mesmo para operadores
 *       administrativos participantes (`gestor`/`supervisor`/`admin_tenant`). Logo, FORA de caucus, a chave `faction:<code>` **não isola
 *       nada hoje** — verificado por execução: um `side='buyer'` vê a mensagem
 *       `['faction:faction-a','bond-anchors','bond-admins']`. A cegueira mútua entre
 *       compartimentos (o valor observável desta fatia) só se realiza no nível de MENSAGEM
 *       quando o WRITER parar de co-emitir `bond-anchors` em mensagem compartimentada.
 *       Isto é PRÉ-EXISTENTE (não introduzido aqui) e é residual DECLARADO (spec §5 R6):
 *       hoje quem isola de fato é só a Camada 4 (caucus) — que é inalcançável (PV3).
 *
 *   ✅ FECHADA (B4b — ordem canônica `[isPrimary desc, joinedAt asc]` nos **TRÊS** lados:
 *       snapshot de leitura, preview e o lookup do REMETENTE em `prepareOutbound`; este último
 *       era o terceiro lado esquecido, achado M4 da revisão) — (7) **Participante multi-linha (achado da re-revisão):** `BondParticipant` NÃO tem
 *       `@@unique(bondId, userId)`. Com 2 linhas ativas do mesmo `userId` no mesmo Bond
 *       (papéis/`side` diferentes), o serviço usa `findFirst` **sem `orderBy`** (ordem
 *       arbitrária do Postgres) e este resolver usa `.find` na **ordem do array do caller**
 *       ⇒ podem escolher linhas diferentes. O fake do differential resolve no MESMO array,
 *       logo é mais determinístico que o Prisma real e o guarda mecânico NÃO cobre este eixo.
 *       O não-determinismo é PRÉ-EXISTENTE do serviço; a promoção deve fixar uma ordem
 *       canônica (`orderBy`) nos dois lados.
 *   ⛔ ABERTA (é a fatia B4c) — (6) **Caucus ↔ compartimento NOVO (residual R7):** `CAUCUS_COMPARTMENT_SEED` mapeia os
 *       2 caucus do enum Prisma aos 2 códigos de PLATAFORMA. Num vínculo cuja policy cunhou
 *       códigos NOVOS, ninguém casa o caucus — nem a parte dona dele (só o neutro passa).
 *       A direção é SEGURA (nega) e o caucus é inalcançável hoje (PV3), mas mapear
 *       caucus↔compartimento novo é a fatia B4c (exige migração: o enum `SessionContext`
 *       tem só 2 caucus). Congelado por teste no differential, não escondido.
 *       **⚠️ Footgun a registrar (inócuo hoje por PV3, mas é o gatilho da B4c):** declarar
 *       uma policy **FECHA o caucus para as próprias partes que ela nomeia** — o primeiro
 *       tenant que ligar policy E caucus ao mesmo tempo veria isso silenciosamente.
 *
 * ★ CONTRATO DO SNAPSHOT (✅ explicitado no wire da B4b — o serviço filtra `revokedAt: null` nas
 * 4 queries do snapshot e o differential ASSERE os filtros, com participante e bubble
 * revogados em cenário): `participants` e `bubbles` devem conter **SÓ REGISTROS ATIVOS**.
 * O serviço vivo aplica `revokedAt: null` em TODAS as suas queries; o resolver NÃO tem esse
 * eixo e trata tudo que recebe como vivo. Pré-filtrar é responsabilidade do caller (os 2
 * consumidores atuais o fazem — o preview filtra `revokedAt: null`, o differential só
 * alimenta ativos). Um consumidor futuro que passe linhas cruas trataria participante ou
 * bubble REVOGADA como viva — divergência com formato de vazamento. Precondição de promoção.
 *
 * Spec: docs/superpowers/specs/2026-07-25-motor-neutro-b4-visibilidade-papel-contraparte-design.md
 */
/**
 * SEED de plataforma — DATA, não lei: é exatamente o que `sideToFaction` faz hoje
 * (`bubble-visibility.service.ts:20-24`). Preservado VERBATIM ⇒ byte-identidade.
 */
export const SIDE_COMPARTMENT_SEED = {
    seller: 'faction-a',
    buyer: 'faction-b',
};
/** Sides estruturalmente NEUTROS (veem todos os compartimentos) — `checkFactionAccess:269`. */
export const NEUTRAL_SIDES = new Set(['mediator', 'neutral']);
/** Caucus → compartimento (seed; `SessionContext` do Prisma só tem 2 hoje — spec §0 PV3). */
export const CAUCUS_COMPARTMENT_SEED = {
    private_caucus_a: 'faction-a',
    private_caucus_b: 'faction-b',
};
/** Contextos de sessão ABERTOS (todos do vínculo veem) — `checkFactionAccess:259`. */
export const OPEN_SESSION_CONTEXTS = new Set(['joint', 'formal']);
/** Papéis de OPERADOR que satisfazem `bond-admins` — `checkNativeVisibilityKey:229`. */
export const BOND_ADMIN_OPERATOR_ROLES = new Set([
    'gestor',
    'supervisor',
    'admin_tenant',
]);
/** Códigos que um compartimento NUNCA pode assumir (colidiriam com chaves nativas). */
export const RESERVED_COMPARTMENT_CODES = new Set([
    'bond-anchors',
    'bond-admins',
]);
/**
 * Códigos CUNHADOS PELA PLATAFORMA (derivados do `side`). São RESERVADOS: a config NÃO
 * pode atribuí-los (regra R5 do piso).
 *
 * ★ Sem isto o piso tem um furo de AMPLIAÇÃO — falsificado por execução na revisão
 * adversarial: `{ comprador: 'faction-a' }` num participante `side='buyer'` movia-o para
 * o compartimento da CONTRAPARTE e ele passava a ler o caucus adverso
 * (`caucus-mismatch` → `caucus-match`); e `{ parte: 'faction-a' }` num `side=null`
 * virava `keys-exhausted` → `key-allowed` na chave `faction:faction-a`. Config só pode
 * cunhar compartimentos NOVOS (estreitar); entrar num compartimento existente da
 * plataforma é decisão de plataforma, não de tenant.
 */
export const PLATFORM_COMPARTMENT_CODES = new Set(Object.values(SIDE_COMPARTMENT_SEED));
/** Prefixos de chave de visibilidade (um código de compartimento não pode contê-los). */
const KEY_PREFIX_PARTICIPANT = 'participant:';
const KEY_PREFIX_OPERATOR_ROLE = 'role:';
const KEY_PREFIX_BOND_ROLE = 'bond-role:';
const KEY_PREFIX_COMPARTMENT = 'faction:';
const KEY_BOND_ANCHORS = 'bond-anchors';
const KEY_BOND_ADMINS = 'bond-admins';
/**
 * Literais de chave que um código de caucus NUNCA pode conter — colidiriam com o vocabulário
 * nativo. Deliberadamente testados por `includes` e não `startsWith`: fail-closed, ao custo de
 * rejeitar um código exótico legítimo que carregue o literal no meio.
 */
const RESERVED_KEY_LITERALS = [
    KEY_PREFIX_PARTICIPANT,
    KEY_PREFIX_OPERATOR_ROLE,
    KEY_PREFIX_BOND_ROLE,
    KEY_PREFIX_COMPARTMENT,
];
/**
 * O PISO do código de caucus (B4c) — 3 regras, na linhagem do Blocker da B4 (regra 5) e do R3
 * da B3. Devolve o código saneado ou `null` (= sem caucus declarado).
 *
 *   **C1** — literais de chave nativa proibidos (`bond-anchors`/`bond-admins` +
 *            `participant:`/`role:`/`bond-role:`/`faction:`).
 *   **C2** — o vocabulário da SESSÃO ABERTA (`joint`/`formal`) é RESERVADO. É a regra que
 *            barra os valores do **próprio vocabulário da plataforma** — a lição literal do
 *            Blocker da B4, aqui do lado da ABERTURA: sem ela um tenant declara um "caucus"
 *            chamado `joint` e ninguém consegue dizer se ele restringe ou abre.
 *   **C3** — não-string/vazio ⇒ `null`. Parse defensivo, para que "total" não fique só no
 *            tipo (lição MINOR-1 da B3) e dado corrompido nunca vire caucus silencioso.
 *
 * ★ NÃO barra `PLATFORM_COMPARTMENT_CODES`, e a assimetria com a regra 5 é deliberada:
 * lá a config MOVERIA um participante para um compartimento de plataforma (ampliação real,
 * o furo que a revisão da B4 falsificou por execução); aqui só se NOMEIA a sessão, sem mexer
 * em participante nenhum — e um vínculo SEM policy precisa poder apontar o caucus para
 * `faction-a`. Apontar para um código que ninguém possui TRANCA a conversa (fail-closed),
 * não amplia: é o residual R19, e o endpoint de autoria o barra com 400.
 */
export function applyCaucusFloor(raw) {
    if (typeof raw !== 'string')
        return null;
    const code = raw.trim();
    if (code === '')
        return null;
    if (RESERVED_COMPARTMENT_CODES.has(code))
        return null;
    if (OPEN_SESSION_CONTEXTS.has(code))
        return null;
    if (RESERVED_KEY_LITERALS.some((literal) => code.includes(literal)))
        return null;
    return code;
}
/**
 * O compartimento a que a SESSÃO está restrita — `null` = sem caucus (ou sessão ABERTA).
 *
 * Precedência **declarado ▸ seed do enum** (o molde override▸seed de A3/A5). O ramo declarado
 * é o **fix do R7**: num vínculo cuja policy cunhou códigos próprios, o seed de 2 valores fazia
 * a Camada 4 negar **até a parte DONA do caucus** — e negar ali curto-circuita o resolver, então
 * nem o `bond-admins` a salvava.
 *
 * Neutralidade: o enum `SessionContext` enumera as PARTES (`private_caucus_a`/`_b`) — o mesmo
 * *commercial-shape* que a B4 achou no `side`. O caucus neutro é "restrita ao compartimento X",
 * com X opaco cunhado pelo vínculo ⇒ N partes sem tocar o enum.
 */
export function resolveCaucusCompartment(input) {
    const declared = applyCaucusFloor(input.sessionCompartment);
    if (declared !== null)
        return declared;
    if (input.sessionContext === null)
        return null;
    // `Object.hasOwn` blinda chave herdada do protótipo (classe C3/M2 — o mesmo fix que a B2
    // aplicou em `modeForType`): `sessionContext: '__proto__'` devolveria `Object.prototype`,
    // truthy e não-string, que o `??` não pega. Inalcançável pelo engine (a coluna é enum
    // Prisma), mas esta primitiva é PURA e exportada.
    if (!Object.hasOwn(CAUCUS_COMPARTMENT_SEED, input.sessionContext))
        return null;
    return CAUCUS_COMPARTMENT_SEED[input.sessionContext] ?? null;
}
/** Normalização do papel-de-vínculo para casamento de policy/chave (trim + lower). */
export function normalizeRoleKeyForVisibility(rawRole) {
    return (rawRole ?? '').trim().toLowerCase();
}
/**
 * Chaves de **pertencimento AMPLO** — subordinadas ao compartimento (piso R6, B4b).
 *
 * `bond-anchors` concede a QUALQUER participante ativo. Numa mensagem compartimentada isso
 * derrota a chave de compartimento (a Camada 5 é OR-mode) e a compartimentação vira
 * decorativa — era o residual **R6 da B4**, verificado por execução.
 *
 * ★ Deliberadamente FORA daqui: `bond-admins` (supervisão de operador administrativo
 * participante — subordiná-la cegaria a supervisão, o oposto do objetivo) e
 * `participant:<id>` (concessão individual, JÁ mais estreita que o compartimento).
 */
export const COMPARTMENT_SUBORDINATE_KEYS = new Set([KEY_BOND_ANCHORS]);
/**
 * A mensagem carrega ≥1 chave de compartimento **NATIVA** neste vínculo?
 *
 * É o predicado do piso R6. Calculado sobre TODO o conjunto de chaves ANTES do laço ⇒ a
 * decisão é **ordem-independente**. Uma chave `faction:<code>` cujo código é desconhecido no
 * vínculo NÃO conta: não há compartimentação a impor (ela cai no fluxo de bubble).
 */
export function hasNativeCompartmentKey(keys, compartmentCodes) {
    return keys.some((key) => key.startsWith(KEY_PREFIX_COMPARTMENT) &&
        compartmentCodes.has(key.slice(KEY_PREFIX_COMPARTMENT.length)));
}
/**
 * As chaves de visibilidade de uma mensagem de saída, dado o compartimento do REMETENTE.
 *
 * Fonte ÚNICA da composição (writer) — o par de `hasNativeCompartmentKey` (leitor).
 * Invariante provada por enumeração: **`bond-anchors` é emitida ⟺ a mensagem NÃO é
 * compartimentada.** `compartment === null` cobre tanto o participante NEUTRO quanto o de
 * compartimento não declarado: em nenhum dos dois há audiência a estreitar.
 */
export function composeCompartmentKeys(compartment) {
    return compartment === null
        ? [KEY_BOND_ANCHORS, KEY_BOND_ADMINS]
        : [`${KEY_PREFIX_COMPARTMENT}${compartment}`, KEY_BOND_ADMINS];
}
/**
 * As chaves de uma mensagem cujo autor é o **SUJEITO** da conversa — a fala do cliente
 * (inbound) e a resposta da IA que fala com ele (B4d; fecha o residual R8 da B4b, que
 * compartimentava só a mensagem de OPERADOR).
 *
 * Difere de `composeCompartmentKeys` (o writer de operador) em UM ramo, e o ramo é a decisão
 * do TL: compartimento não resolvido ⇒ **nenhuma chave**, byte-idêntico ao que se grava hoje.
 * Carimbar `['bond-anchors','bond-admins']` no indefinido — o que o writer de operador faz —
 * ESTREITARIA: o resolver nega todo viewer com `operatorRole === null`, e os leitores internos
 * de IA (`living-summary`, `classification`, `behavioral-analysis`, `checklist-agent`) chamam
 * o filtro com o sentinela `'system'`, que não é um `User` ⇒ ficariam cegos em TODA conversa
 * adversarial não-atribuída, e não só nas efetivamente compartimentadas (spec B4d §0 PX-A).
 *
 * Invariante (por enumeração no spec): `keys.length === 0 ⟺ compartment === null`. Corolário —
 * herda o piso **R6** da B4b de graça: com compartimento resolvido, `bond-anchors` (a chave que
 * libera qualquer participante ativo) nunca é emitida.
 */
export function composeSubjectCompartmentKeys(compartment) {
    return compartment === null ? [] : composeCompartmentKeys(compartment);
}
/**
 * Extrai a policy de `Bond.metadata` (Json livre) — ZERO migração.
 *
 * `Object.hasOwn` blinda chave herdada do protótipo (classe C3 M2 — o achado I1 do Fable na
 * B2). Qualquer coisa fora do formato ⇒ `null` ⇒ o resolver cai no seed ⇒ conduta de hoje.
 */
export function extractVisibilityPolicy(metadata) {
    if (metadata === null || typeof metadata !== 'object' || Array.isArray(metadata))
        return null;
    const record = metadata;
    if (!Object.hasOwn(record, 'visibilityPolicy'))
        return null;
    return parseVisibilityPolicy(record.visibilityPolicy);
}
// ─── O PISO INAFROUXÁVEL (spec §2) ──────────────────────────────────────────────
/**
 * Piso: config só pode ESTREITAR audiência.
 *
 *   R1 — NUNCA concede neutralidade (nenhum valor produz `neutral: true`; a neutralidade
 *        é resolvida antes, e só a partir do `side`).
 *   R2 — NUNCA apaga o compartimento do seed (valor inválido/vazio ⇒ mantém o seed).
 *   R3 — NUNCA age sobre participante NEUTRO (intocável nas DUAS direções — a peça
 *        estrutural do vínculo é o `side`, não a policy). Aplicada pelo caller.
 *   R4 — Códigos RESERVADOS ou contendo `:` são rejeitados (não se disfarçam de chave).
 *   R5 — Códigos CUNHADOS PELA PLATAFORMA (`PLATFORM_COMPARTMENT_CODES`, derivados do
 *        `side`) são rejeitados: a config só pode cunhar compartimentos NOVOS. **Esta é a
 *        regra que fecha a AMPLIAÇÃO** — sem ela, `{ comprador: 'faction-a' }` movia um
 *        `side='buyer'` para o compartimento da contraparte e ele lia o caucus adverso
 *        (furo falsificado por execução na revisão adversarial).
 *
 * INERTE sobre o seed: sem configuração válida, devolve o próprio compartimento do seed.
 *
 * ★ A garantia que estas 5 regras SUSTENTAM: **nenhuma config admissível amplia a audiência
 * de um participante sobre um compartimento CUNHADO PELA PLATAFORMA** (o que é alcançável
 * hoje, pois `prepareOutbound` só emite `faction:faction-a|b`). Precisando o efeito, sem
 * arredondar: config pode (a) **cunhar um compartimento NOVO** — o que ESTREITA a Camada 4
 * (deixa o ramo "não declarado", que passava, e passa a exigir match) e ao mesmo tempo torna
 * NATIVA a chave `faction:<novo>` da Camada 5, que passa a CONCEDER a quem está nele
 * (inclusive por cima de uma bubble homônima que negaria — precondição 1); ou (b) mover
 * alguém entre compartimentos NOVOS, reagrupando audiências — o caso da precondição 2
 * (portão de autoria). Nenhum dos dois é alcançável no caminho vivo hoje (nenhuma mensagem
 * carrega chave de compartimento novo).
 */
export function applyCompartmentFloor(seed, configured) {
    // R3 — neutro é intocável (defense-in-depth; o caller já não chama neste caso).
    if (seed.neutral)
        return null;
    if (typeof configured !== 'string')
        return seed.compartment;
    const code = configured.trim();
    // R2 — vazio nunca apaga o seed.
    if (code === '')
        return seed.compartment;
    // R4 — reservados e qualquer coisa com `:` (prefixo de chave) são rejeitados.
    if (RESERVED_COMPARTMENT_CODES.has(code) || code.includes(':'))
        return seed.compartment;
    // R5 — a config não entra num compartimento cunhado pela PLATAFORMA (anti-ampliação).
    if (PLATFORM_COMPARTMENT_CODES.has(code))
        return seed.compartment;
    return code;
}
/**
 * Compartimento de um participante: neutralidade de plataforma ▸ policy do vínculo ▸ seed
 * do `side` ▸ não declarado. `applyCompartmentFloor` roda SEMPRE sobre o valor configurado.
 */
export function resolveParticipantCompartment(participant, policy) {
    const side = participant.side;
    // Neutralidade estrutural — privilégio de plataforma, resolvido ANTES da config (piso R3).
    if (side !== null && NEUTRAL_SIDES.has(side)) {
        return { compartment: null, neutral: true, source: 'neutral-side' };
    }
    const seedCode = side !== null ? (SIDE_COMPARTMENT_SEED[side] ?? null) : null;
    const seed = {
        compartment: seedCode,
        neutral: false,
        source: seedCode !== null ? 'side-seed' : 'undeclared',
    };
    const table = policy?.compartmentByRole;
    if (table === undefined)
        return seed;
    const roleKey = normalizeRoleKeyForVisibility(participant.rawRole);
    if (roleKey === '' || !Object.hasOwn(table, roleKey))
        return seed;
    const floored = applyCompartmentFloor(seed, table[roleKey]);
    if (floored === seed.compartment)
        return seed;
    return { compartment: floored, neutral: false, source: 'role-policy' };
}
/**
 * Os códigos de compartimento CONHECIDOS deste vínculo — é o que torna uma chave
 * `faction:<code>` **nativa**. SEM policy o conjunto é exatamente `{faction-a, faction-b}`
 * (o seed só produz esses dois), reproduzindo o teste literal do serviço ⇒ byte-idêntico.
 */
export function resolveCompartmentCodes(participants, policy) {
    const codes = new Set(Object.values(SIDE_COMPARTMENT_SEED));
    for (const participant of participants) {
        const resolved = resolveParticipantCompartment(participant, policy);
        if (resolved.compartment !== null)
            codes.add(resolved.compartment);
    }
    return codes;
}
/**
 * A matriz papel × audiência — o "papel×contraparte" OBSERVÁVEL (consumida pelo preview).
 * `sees: 'all'` = neutro OU compartimento não declarado (o caso em que o participante não
 * está compartimentado); senão o único compartimento a que pertence.
 */
export function resolveAudienceMatrix(participants, policy) {
    return participants.map((participant) => {
        const resolved = resolveParticipantCompartment(participant, policy);
        return {
            rawRole: participant.rawRole,
            compartment: resolved.compartment,
            neutral: resolved.neutral,
            sees: resolved.compartment === null ? 'all' : [resolved.compartment],
        };
    });
}
/**
 * Parse DEFENSIVO da policy vinda de `Bond.metadata.visibilityPolicy` (Json livre).
 * Nunca lança; descarta entradas inválidas uma a uma; sem nenhuma entrada válida ⇒ `null`
 * (⇒ o resolver cai no seed ⇒ comportamento de hoje). `Object.hasOwn` blinda chaves
 * herdadas do protótipo (classe C3 M2 — o achado I1 do Fable na B2).
 */
export function parseVisibilityPolicy(raw) {
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw))
        return null;
    const table = raw.compartmentByRole;
    if (table === null || typeof table !== 'object' || Array.isArray(table))
        return null;
    const parsed = {};
    const entries = table;
    for (const key of Object.keys(entries)) {
        if (!Object.hasOwn(entries, key))
            continue;
        const value = entries[key];
        if (typeof value !== 'string')
            continue;
        const code = value.trim();
        if (code === '')
            continue;
        const roleKey = normalizeRoleKeyForVisibility(key);
        if (roleKey === '')
            continue;
        parsed[roleKey] = code;
    }
    return Object.keys(parsed).length === 0 ? null : { compartmentByRole: parsed };
}
function activeParticipantOf(input) {
    return input.participants.find((p) => p.userId === input.viewer.userId) ?? null;
}
/** Camada 5 — uma bubble libera o viewer? Espelha `userAllowedByBubble`. */
function bubbleAllows(bubble, userId, operatorRole) {
    if (bubble.createdByUserId === userId)
        return true;
    if (bubble.visibilityMode === 'only_creator')
        return false;
    if (bubble.visibilityMode === 'creator_and_specific_roles') {
        return bubble.allowedRoles.includes(operatorRole);
    }
    if (bubble.visibilityMode === 'creator_and_list') {
        return bubble.allowedUserIds.includes(userId);
    }
    return false;
}
/**
 * Resolve UMA chave de visibilidade. Espelha `checkNativeVisibilityKey` + o ramo `faction:`,
 * e ACRESCENTA o namespace NOVO `bond-role:` (papel de VÍNCULO — a leitura que a ADR-040
 * escreve para `role:` e que o código implementou como papel de OPERADOR, spec §0 PV2).
 *
 * `'deny'` = esta chave não concede (outra ainda pode — OR-mode).
 * `'not-native'` = delega ao fluxo de bubble/consent.
 */
function resolveKey(key, input, operatorRole, compartmentCodes, compartmentKeyPresent) {
    const { viewer } = input;
    if (key.startsWith(KEY_PREFIX_PARTICIPANT)) {
        const target = key.slice(KEY_PREFIX_PARTICIPANT.length);
        return target === viewer.userId || (viewer.personId !== null && target === viewer.personId)
            ? 'allow'
            : 'deny';
    }
    // `bond-role:` ANTES de `role:` — `role:` não é prefixo de `bond-role:`, mas a ordem
    // documenta a precedência e protege contra futura mudança de literal.
    if (key.startsWith(KEY_PREFIX_BOND_ROLE)) {
        const target = normalizeRoleKeyForVisibility(key.slice(KEY_PREFIX_BOND_ROLE.length));
        if (target === '')
            return 'deny';
        const participant = activeParticipantOf(input);
        if (participant === null)
            return 'deny';
        return normalizeRoleKeyForVisibility(participant.rawRole) === target ? 'allow' : 'deny';
    }
    if (key.startsWith(KEY_PREFIX_OPERATOR_ROLE)) {
        return key.slice(KEY_PREFIX_OPERATOR_ROLE.length) === operatorRole ? 'allow' : 'deny';
    }
    if (key.startsWith(KEY_PREFIX_COMPARTMENT)) {
        const code = key.slice(KEY_PREFIX_COMPARTMENT.length);
        // Nativa SÓ se o código é conhecido neste vínculo (sem policy = os 2 do seed).
        if (!compartmentCodes.has(code))
            return 'not-native';
        const participant = activeParticipantOf(input);
        if (participant === null)
            return 'deny';
        const resolved = resolveParticipantCompartment(participant, input.policy);
        if (resolved.neutral)
            return 'allow';
        // ★ PV8: compartimento NÃO declarado NEGA aqui (≠ Camada 4, que passa). Fiel a `:215`.
        return resolved.compartment === code ? 'allow' : 'deny';
    }
    if (key === KEY_BOND_ANCHORS) {
        // ★ PISO R6 (B4b): em mensagem compartimentada, pertencer ao vínculo NÃO basta —
        // senão a chave de compartimento é decorativa (era o residual R6 da B4).
        if (compartmentKeyPresent)
            return 'deny';
        return activeParticipantOf(input) !== null ? 'allow' : 'deny';
    }
    if (key === KEY_BOND_ADMINS) {
        if (!BOND_ADMIN_OPERATOR_ROLES.has(operatorRole))
            return 'deny';
        return activeParticipantOf(input) !== null ? 'allow' : 'deny';
    }
    return 'not-native';
}
/**
 * A decisão completa, PURA e TOTAL — a ordem espelha `canAccessContext` passo a passo
 * (spec §1.3). Nunca lança. Fail-closed: chaves presentes e nenhuma concedendo ⇒ nega.
 */
export function resolveVisibilityDecision(input) {
    const keys = input.message.visibilityKeys;
    const sessionContext = input.message.sessionContext;
    // B4c — o compartimento da sessão, por precedência (declarado ▸ seed do enum).
    const caucusCompartment = resolveCaucusCompartment({
        sessionCompartment: input.message.sessionCompartment,
        sessionContext,
    });
    // Passo 1 — fast-path (o caso NetComet: sem restrição nenhuma).
    // ★ B4c: `caucusCompartment` entra no guard — senão uma mensagem SEM chaves dentro de um
    // caucus escaparia daqui como `unrestricted`. É ESTREITAMENTO (o fast-path deixa de
    // liberar um caso que hoje libera), e é inerte enquanto não houver caucus no dado.
    if (keys.length === 0 && sessionContext === null && caucusCompartment === null) {
        return { allowed: true, reason: 'unrestricted' };
    }
    // Passo 2 — conversa sem vínculo: sem restrição adversarial possível.
    if (!input.bondPresent) {
        return keys.length === 0
            ? { allowed: true, reason: 'no-bond-unrestricted' }
            : { allowed: false, reason: 'no-bond-keys-denied' };
    }
    // Passo 3 — o modo do vínculo é o liga/desliga da Camada 4 (P9: nada além disso).
    const isAdversarial = input.bond !== null && input.bond.adversarialMode !== 'none';
    // Passo 4 — Camada 4 (compartimento da sessão / caucus).
    //
    // ★ B4c, duas mudanças e ambas SÓ-ESTREITAM:
    //   (a) ativa também no caucus NEUTRO (compartimento declarado sem valor de enum);
    //   (b) compara contra o compartimento RESOLVIDO em vez do `CAUCUS_COMPARTMENT_SEED` cru.
    //       A Camada 4 continua NEGANDO — só passa a negar contra o código certo. É assim que
    //       o R7 morre sem que nenhuma negação seja removida: antes, num vínculo cuja policy
    //       cunhou códigos próprios, `caucus-mismatch` negava até a parte DONA do caucus.
    //
    // Lembrete de mecânica (PY6 da spec): com chaves presentes esta camada só NEGA — os ramos
    // positivos são todos guardados por `keys.length === 0`, e quem concede é a Camada 5.
    if (isAdversarial && (sessionContext !== null || caucusCompartment !== null)) {
        if (sessionContext !== null && OPEN_SESSION_CONTEXTS.has(sessionContext)) {
            // Sessão aberta libera de imediato — inclusive as chaves (espelha o `return true`
            // de `checkFactionAccess`, que só é seguido pela Camada 5 se houver chaves).
            if (keys.length === 0)
                return { allowed: true, reason: 'caucus-open' };
        }
        else {
            const participant = activeParticipantOf(input);
            if (participant === null) {
                return { allowed: false, reason: 'caucus-no-participant' };
            }
            const resolved = resolveParticipantCompartment(participant, input.policy);
            if (resolved.neutral) {
                if (keys.length === 0)
                    return { allowed: true, reason: 'caucus-neutral' };
            }
            else if (resolved.compartment === null) {
                // ★ PV8: não declarado PASSA na Camada 4 (fiel a `:268`).
                if (keys.length === 0)
                    return { allowed: true, reason: 'caucus-undeclared' };
            }
            else if (resolved.compartment === caucusCompartment) {
                if (keys.length === 0)
                    return { allowed: true, reason: 'caucus-match' };
            }
            else {
                return { allowed: false, reason: 'caucus-mismatch' };
            }
        }
    }
    // Passo 5 — Camada 5 (chaves, OR-mode).
    if (keys.length > 0) {
        const operatorRole = input.viewer.operatorRole;
        if (operatorRole === null)
            return { allowed: false, reason: 'no-operator-role' };
        const compartmentCodes = resolveCompartmentCodes(input.participants, input.policy);
        const compartmentKeyPresent = hasNativeCompartmentKey(keys, compartmentCodes);
        for (const key of keys) {
            const native = resolveKey(key, input, operatorRole, compartmentCodes, compartmentKeyPresent);
            if (native === 'allow')
                return { allowed: true, reason: 'key-allowed' };
            if (native === 'deny')
                continue;
            // Não-nativa → bubble ativa com esta chave.
            const bubble = input.bubbles.find((b) => b.visibilityKey === key);
            if (bubble === undefined)
                continue; // órfã — outra chave decide
            if (bubbleAllows(bubble, input.viewer.userId, operatorRole)) {
                return { allowed: true, reason: 'key-allowed' };
            }
            // Bubble nega → consentimento explícito pode destravar.
            if (input.consentedKeys.includes(key)) {
                return { allowed: true, reason: 'key-allowed' };
            }
        }
        return { allowed: false, reason: 'keys-exhausted' };
    }
    return { allowed: true, reason: 'allowed' };
}
/**
 * A sessão RESTRITA desta conversa, do ponto de vista de UM viewer — `null` = não está em caucus.
 *
 * **Não é predicado novo: é DERIVAÇÃO.** A titularidade sai de uma sonda no
 * `resolveVisibilityDecision` (a fonte-de-verdade única desde a B4b) sobre a **mensagem CANÔNICA da
 * sessão** — as chaves de `composeCompartmentKeys(compartment)`, a mesma composição que
 * `prepareOutbound`/`prepareSubjectMessage` usam:
 *
 *   > o código aparece ⟺ este viewer leria a mensagem canônica desta sessão.
 *
 * **Escopo, dito porque a 2ª revisão adversarial o exigiu:** os writers escolhem o ARGUMENTO dessa
 * composição pelo **AUTOR** da mensagem (`caucusStamp` prefere o compartimento do autor e apenas
 * LOGA `[caucus-author-mismatch]`), não pela sessão. Sessão e autor coincidem quando o compartimento
 * do autor casa com o da sessão — e abrir um caucus que NÃO casa com o sujeito da conversa deixou de
 * ser possível (3ª camada do portão em `session-compartment.service.ts`, recusa 400). Resta o caso
 * de um OPERADOR de outro compartimento falando na sessão: a mensagem dele nasce com a chave DELE, e
 * a divulgação — que é por SESSÃO — pode discordar da leitura daquela mensagem específica. É o
 * residual **R-P**, na mesma família do R-O.
 *
 * ★ **A 1ª redação desta função sondava SEM chaves e lia a RAZÃO da Camada 4 — e estava errada.**
 * A revisão adversarial mostrou por execução que mensagem sem chaves dentro de um caucus **não
 * existe** (os dois writers sempre carimbam), então a sonda keyless media uma hipótese: um
 * participante de compartimento NÃO-DECLARADO passava na Camada 4 (`caucus-undeclared`, PV8) e
 * recebia o código, mas era negado na Camada 5 da mensagem real (`keys-exhausted`) — recebia o
 * rótulo de um caucus cujo conteúdo não lê. O caminho era alcançável por API: basta entrar um
 * participante com papel fora do `compartmentByRole` depois de o caucus ser aberto.
 *
 * ★ **E o fix óbvio — tirar `caucus-undeclared` do conjunto — abriria o gap INVERSO**, também
 * medido: o mesmo participante não-declarado, com papel de operador ADMIN, **LÊ** a mensagem real
 * via `bond-admins` (a Camada 4 não retorna com chaves presentes; a Camada 5 o libera). Suprimir o
 * código dele esconderia o rótulo de quem lê o conteúdo — a degradação que o R17 da B4e nomeou.
 * Sondar pelas chaves canônicas resolve os DOIS lados sem conjunto de razões nenhum.
 *
 * **Piso (só estreita):**
 *   - **P1** código non-null só quando a sonda canônica LIBERA;
 *   - **P2** `null` quando a sessão não é RESTRITA — nunca um `{ compartment: null }` vazio, que
 *     faria a tela anunciar caucus onde não há;
 *   - **P3** o retorno carrega só o código de compartimento. **Zero PII**;
 *   - **P4** esta função **não participa** de `canAccess`/`filterMessages`. É derivada read-only e
 *     a leitura de mensagem segue byte-idêntica.
 *
 * Spec: docs/superpowers/specs/2026-07-25-motor-neutro-b4c-b-wire-caucus-design.md §3.1
 */
export function resolveCaucusDisclosure(input) {
    // `applyCaucusFloor` é idempotente sobre a própria saída, então flooreá-lo aqui deixa o tipo
    // honesto (`string | null`) para a sonda sem alterar a semântica da precedência.
    const declared = applyCaucusFloor(input.session.sessionCompartment);
    const compartment = resolveCaucusCompartment({
        sessionCompartment: declared,
        sessionContext: input.session.sessionContext,
    });
    // P2 — presença ⟺ a sessão é RESTRITA. Sessão ABERTA resolve `null` e não é caucus.
    if (compartment === null)
        return null;
    const decision = resolveVisibilityDecision({
        viewer: input.viewer,
        message: {
            // ★ As chaves CANÔNICAS da sessão — as MESMAS que `composeCompartmentKeys` emite e que
            //   `prepareOutbound`/`prepareSubjectMessage` gravam em toda mensagem nascida no caucus.
            visibilityKeys: composeCompartmentKeys(compartment),
            sessionContext: input.session.sessionContext,
            sessionCompartment: declared,
        },
        bondPresent: input.bondPresent,
        bond: input.bond,
        participants: input.participants,
        // Deliberadamente VAZIOS: a divulgação ignora bubble e consentimento (residual R-O). Uma
        // concessão pontual pode fazer alguém LER uma mensagem específica sem receber o rótulo —
        // direção fail-closed, e a alternativa (buscar bubble/consent no caminho do detalhe) trocaria
        // um rótulo por um custo de query em toda abertura de conversa em caucus.
        bubbles: [],
        consentedKeys: [],
        policy: input.policy ?? null,
    });
    // P1 — o STRIP. Sem esta linha o código vazaria para quem não lê a mensagem da sessão.
    return { compartment: decision.allowed ? compartment : null };
}
//# sourceMappingURL=visibility-graph.js.map